import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';


const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.detail || payload.error || `Request failed (${response.status})`);
  return payload;
}

const terminalStatuses = new Set(['succeeded', 'partial', 'failed', 'review_required']);

function statusStyle(status) {
  if (status === 'succeeded') return 'border-green-200 bg-green-50';
  if (status === 'review_required') return 'border-red-300 bg-red-50';
  if (status === 'failed' || status === 'partial') return 'border-amber-300 bg-amber-50';
  return 'border-indigo-200 bg-indigo-50';
}

export default function CMECFCaseFilingPanel({ caseId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [filing, setFiling] = useState(null);
  const [debnChoices, setDebnChoices] = useState({});
  const [meansTest, setMeansTest] = useState({});
  const [selectedStepKeys, setSelectedStepKeys] = useState([]);
  const [feeAmount, setFeeAmount] = useState('0.00');
  const [filingAcknowledged, setFilingAcknowledged] = useState(false);
  const [redactionAcknowledged, setRedactionAcknowledged] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [error, setError] = useState('');
  const [resolvingCaseNumber, setResolvingCaseNumber] = useState('');
  const notifiedRun = useRef('');

  const loadPreflight = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/case/${caseId}/cmecf-case-filing/preflight/`);
      setPreflight(data);
      const savedSelections = data.latest_filing?.selections || {};
      setDebnChoices(savedSelections.debn_choices || Object.fromEntries(
        (data.debtors || []).map(debtor => [String(debtor.id), debtor.debn_choice])
      ));
      setMeansTest({ ...(data.means_test || {}), ...(savedSelections.means_test || {}) });
      setFeeAmount(savedSelections.initial_filing_fee_amount || data.initial_filing_fee_amount || '0.00');
      const latest = data.latest_filing;
      const initialComplete = (latest?.steps || []).some(
        step => step.key === 'initial_case' && step.status === 'succeeded'
      );
      setSelectedStepKeys(
        latest?.can_resume && initialComplete
          ? (latest.steps || [])
            .filter(step => step.key !== 'initial_case' && !['succeeded', 'skipped'].includes(step.status))
            .map(step => step.key)
          : []
      );
      setFiling(latest && ['queued', 'in_progress', 'review_required'].includes(latest.status) ? latest : null);
      setResolvingCaseNumber(latest?.court_case_number || data.existing_case_number || '');
      setFilingAcknowledged(false);
      setRedactionAcknowledged(false);
      setConfirmationText('');
    } catch (requestError) {
      setError(requestError.message || 'Could not load the filing review.');
    } finally {
      setLoading(false);
    }
  };

  const openReview = () => {
    setOpen(true);
    loadPreflight();
  };

  useEffect(() => {
    if (!open || !filing?.id || !['queued', 'in_progress'].includes(filing.status)) return undefined;
    let cancelled = false;
    let polling = false;
    const poll = async () => {
      if (cancelled || polling) return;
      polling = true;
      try {
        const current = await apiFetch(`/firm/cmecf-case-filings/${filing.id}/`);
        if (cancelled) return;
        setFiling(current);
        if (terminalStatuses.has(current.status) && notifiedRun.current !== current.id) {
          notifiedRun.current = current.id;
          onChanged?.();
        }
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Could not check the filing status.');
      } finally {
        polling = false;
      }
    };
    poll();
    const timer = window.setInterval(poll, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [filing?.id, filing?.status, onChanged, open]);

  const openComponent = async document => {
    const viewer = window.open('', '_blank');
    if (viewer) viewer.opener = null;
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}${document.view_url}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('The saved filing document could not be opened.');
      const objectUrl = URL.createObjectURL(await response.blob());
      if (viewer) viewer.location = objectUrl;
      else window.open(objectUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (requestError) {
      if (viewer) viewer.close();
      setError(requestError.message);
    }
  };

  const activeOrReview = filing && ['queued', 'in_progress', 'review_required'].includes(filing.status);
  const latest = preflight?.latest_filing;
  const resumeRun = latest?.can_resume && latest.package_id === preflight?.package?.id ? latest : null;
  const initialStepComplete = (resumeRun?.steps || []).some(
    step => step.key === 'initial_case' && step.status === 'succeeded'
  );
  const selectableRetrySteps = initialStepComplete
    ? (resumeRun?.steps || []).filter(
      step => step.key !== 'initial_case' && !['succeeded', 'skipped'].includes(step.status)
    )
    : [];
  const selectiveRetry = Boolean(resumeRun && initialStepComplete);
  const displayedSteps = resumeRun?.steps || preflight?.steps || [];
  const debnComplete = (preflight?.debtors || []).every(
    debtor => ['enroll', 'decline'].includes(debnChoices[String(debtor.id)])
  );
  const canStart = Boolean(
    preflight?.ready
    && debnComplete
    && filingAcknowledged
    && redactionAcknowledged
    && confirmationText.trim().toUpperCase() === 'FILE'
    && !activeOrReview
    && (!selectiveRetry || selectedStepKeys.length > 0)
  );

  const startFiling = async () => {
    setLoading(true);
    setError('');
    try {
      const current = await apiFetch(`/case/${caseId}/cmecf-case-filing/start/`, {
        method: 'POST',
        body: JSON.stringify({
          resume_filing_id: resumeRun?.id || undefined,
          debn_choices: debnChoices,
          means_test: meansTest,
          selected_step_keys: selectiveRetry ? selectedStepKeys : undefined,
          initial_filing_fee_amount: feeAmount,
          filing_acknowledged: filingAcknowledged,
          redaction_acknowledged: redactionAcknowledged,
          confirmation_text: confirmationText,
        }),
      });
      setFiling(current);
    } catch (requestError) {
      setError(requestError.message || 'The filing could not be started.');
    } finally {
      setLoading(false);
    }
  };

  const resolveUncertain = async outcome => {
    const uncertain = (filing?.steps || []).find(step => step.status === 'review_required');
    const question = outcome === 'filed'
      ? 'Confirm only after checking the live CM/ECF docket. Record this item as filed?'
      : 'Confirm only after checking CM/ECF and verifying this item was not filed. Allow it to be retried?';
    if (!window.confirm(question)) return;
    setLoading(true);
    setError('');
    try {
      const current = await apiFetch(`/firm/cmecf-case-filings/${filing.id}/resolve/`, {
        method: 'POST',
        body: JSON.stringify({
          outcome,
          case_number: uncertain?.key === 'initial_case' ? resolvingCaseNumber : undefined,
        }),
      });
      setFiling(current);
      await loadPreflight();
      setFiling(current);
      onChanged?.();
    } catch (requestError) {
      setError(requestError.message || 'The docket check could not be saved.');
    } finally {
      setLoading(false);
    }
  };

  const cancelQueued = async () => {
    if (!window.confirm('Cancel this stalled queue request? This does not reverse anything already filed.')) return;
    setLoading(true);
    setError('');
    try {
      const current = await apiFetch(`/firm/cmecf-case-filings/${filing.id}/cancel-queued/`, {
        method: 'POST',
      });
      setFiling(current);
      await loadPreflight();
    } catch (requestError) {
      setError(requestError.message || 'The queued filing could not be cancelled.');
    } finally {
      setLoading(false);
    }
  };

  const visibleDocuments = useMemo(
    () => (preflight?.package?.manifest || []).filter(item => item.view_url),
    [preflight?.package?.manifest]
  );

  return (
    <>
      <button
        type="button"
        onClick={openReview}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-700 px-5 py-3 font-bold text-white shadow hover:bg-red-800"
      >
        <LockClosedIcon className="h-5 w-5" />
        File newest package with CM/ECF
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-start justify-between border-b border-slate-200 bg-white px-6 py-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{selectiveRetry ? 'File unfinished ancillary documents' : 'File the case and ancillary documents'}</h2>
                <p className="mt-1 text-sm text-slate-500">This uses the newest saved filing package only.</p>
              </div>
              <button type="button" onClick={() => !activeOrReview && setOpen(false)} disabled={activeOrReview} className="rounded-full p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-40">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6 p-6">
              <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 text-red-950">
                <div className="flex gap-3">
                  <ExclamationTriangleIcon className="h-7 w-7 shrink-0 text-red-700" />
                  <div>
                    <h3 className="font-bold">Live court filing</h3>
                    <p className="mt-1 text-sm leading-6">{selectiveRetry
                      ? 'The bankruptcy case already exists. Only the unfinished ancillary documents you select below will be submitted.'
                      : 'The first court submission creates the bankruptcy case and may create a filing-fee obligation. Each later item is filed as a separate docket event.'}</p>
                  </div>
                </div>
              </div>

              {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">{error}</div>}
              {loading && !preflight && <p className="py-8 text-center text-slate-600">Loading the filing review…</p>}

              {preflight && !filing && (
                <>
                  {!preflight.package && <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">Create and save a Complete Filing Package first.</div>}
                  {preflight.package_error && <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-900">{preflight.package_error}</div>}
                  {preflight.initial_already_filed && <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">This case already has a court case number, so a second initial filing is blocked.</div>}
                  {!preflight.credentials?.ready_to_file && <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">Finish and enable CM/ECF Automatic Filing on Account Management first.</div>}

                  {preflight.package && (
                    <section>
                      <h3 className="font-semibold text-slate-900">1. Review the newest package</h3>
                      <p className="mt-1 text-sm text-slate-600">{preflight.package.filename} · {new Date(preflight.package.created_at).toLocaleString()}</p>
                      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {visibleDocuments.map(document => (
                          <button key={document.filename} type="button" onClick={() => openComponent(document)} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-indigo-700 hover:bg-indigo-50">
                            <DocumentMagnifyingGlassIcon className="h-5 w-5 shrink-0" />
                            <span className="truncate">{document.filename}</span>
                          </button>
                        ))}
                      </div>
                    </section>
                  )}

                  <section>
                    <h3 className="font-semibold text-slate-900">2. {selectiveRetry ? 'Choose unfinished documents' : 'Filing sequence'}</h3>
                    {selectiveRetry && (
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm text-slate-600">Filed items are locked to prevent accidental duplicate filings.</p>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setSelectedStepKeys(selectableRetrySteps.map(step => step.key))} className="rounded-md border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Select all unfinished</button>
                          <button type="button" onClick={() => setSelectedStepKeys([])} className="rounded-md border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Clear</button>
                        </div>
                      </div>
                    )}
                    <ol className="mt-3 space-y-2">
                      {displayedSteps.map((step, index) => {
                        const completed = ['succeeded', 'skipped'].includes(step.status);
                        const selectable = selectiveRetry
                          && step.key !== 'initial_case'
                          && !completed;
                        const selected = selectedStepKeys.includes(step.key);
                        return (
                        <li key={step.key} className="rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-700">
                          <label className={`flex items-start gap-3 ${selectable ? 'cursor-pointer' : ''}`}>
                            {selectiveRetry && step.key !== 'initial_case' && (
                              <input
                                type="checkbox"
                                checked={selectable ? selected : completed}
                                disabled={!selectable}
                                onChange={event => setSelectedStepKeys(current => (
                                  event.target.checked
                                    ? [...new Set([...current, step.key])]
                                    : current.filter(key => key !== step.key)
                                ))}
                                className="mt-1"
                              />
                            )}
                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center justify-between gap-2">
                                <strong>{index + 1}. {step.label}</strong>
                                {selectiveRetry && (
                                  <span className={completed ? 'font-semibold text-green-700' : 'text-amber-700'}>
                                    {step.status === 'succeeded' ? 'Filed' : step.status === 'skipped' ? 'Not required' : 'Unfinished'}
                                  </span>
                                )}
                              </span>
                              <span className="mt-1 block text-xs text-slate-500">{(step.filenames || []).join(' + ')}</span>
                            </span>
                          </label>
                        </li>
                        );
                      })}
                    </ol>
                    {preflight.chapter_13_plan_note && <p className="mt-3 rounded-lg bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">{preflight.chapter_13_plan_note}</p>}
                  </section>

                  <section>
                    <h3 className="font-semibold text-slate-900">3. DEBN choice for each debtor</h3>
                    <p className="mt-1 text-sm text-slate-600">Each DEBN is filed separately using the choice saved in this package. To change a choice, create a new package so the PDF and court selection stay identical.</p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {(preflight.debtors || []).map(debtor => (
                        <div key={debtor.id} className="rounded-lg border border-slate-200 p-4">
                          <div className="font-medium text-slate-900">{debtor.label}: {debtor.name}</div>
                          <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                            {debnChoices[String(debtor.id)] === 'enroll'
                              ? 'Enroll in Debtor Electronic Noticing'
                              : 'Decline Debtor Electronic Noticing'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {preflight.chapter === '7' && (
                    <section className="rounded-xl border border-slate-200 p-4">
                      <h3 className="font-semibold text-slate-900">4. Form 122A-1 court answers</h3>
                      <p className="mt-1 text-sm text-slate-600">These answers belong only to the separate Current Monthly Income filing event.</p>
                      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <label className="text-sm text-slate-700">Exempt due to business debt or qualifying military service?
                          <select value={meansTest.exempt_from_presumption ? 'yes' : 'no'} onChange={event => setMeansTest(current => ({ ...current, exempt_from_presumption: event.target.value === 'yes' }))} className="mt-1 w-full rounded-md border-slate-300">
                            <option value="no">No</option><option value="yes">Yes</option>
                          </select>
                        </label>
                        <label className="text-sm text-slate-700">Presumption status
                          <select value={meansTest.presumption_status || 'no'} onChange={event => setMeansTest(current => ({ ...current, presumption_status: event.target.value }))} className="mt-1 w-full rounded-md border-slate-300">
                            <option value="no">No</option><option value="unknown">Unknown</option>
                          </select>
                        </label>
                        <label className="text-sm text-slate-700">Form 122A-1 line 11
                          <input type="number" min="0" step="0.01" value={meansTest.line_11_current_monthly_income || ''} onChange={event => setMeansTest(current => ({ ...current, line_11_current_monthly_income: event.target.value }))} className="mt-1 w-full rounded-md border-slate-300" />
                        </label>
                      </div>
                    </section>
                  )}

                  {preflight.chapter === '7' && preflight.has_long_means_test && (
                    <section className="rounded-xl border border-slate-200 p-4">
                      <h3 className="font-semibold text-slate-900">Form 122A-2 court answer</h3>
                      <p className="mt-1 text-sm text-slate-600">The long-form calculation will be filed independently under the Chapter 7 Means Test Calculation 122A-2 event.</p>
                      <label className="mt-3 block max-w-sm text-sm text-slate-700">Does the completed Form 122A-2 show a presumption of abuse?
                        <select value={meansTest.long_form_presumption ? 'yes' : 'no'} onChange={event => setMeansTest(current => ({ ...current, long_form_presumption: event.target.value === 'yes' }))} className="mt-1 w-full rounded-md border-slate-300">
                          <option value="no">No</option><option value="yes">Yes</option>
                        </select>
                      </label>
                    </section>
                  )}

                  {preflight.chapter === '13' && (
                    <section className="rounded-xl border border-slate-200 p-4">
                      <h3 className="font-semibold text-slate-900">4. Form 122C-1 court answers</h3>
                      <p className="mt-1 text-sm text-slate-600">These answer the three additional CM/ECF screens shown after the Chapter 13 PDF upload.</p>
                      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <label className="text-sm text-slate-700">Form 122C-1 line 14
                          <input type="number" min="0" step="0.01" value={meansTest.line_14_current_monthly_income || ''} onChange={event => setMeansTest(current => ({ ...current, line_14_current_monthly_income: event.target.value }))} className="mt-1 w-full rounded-md border-slate-300" />
                        </label>
                        <label className="text-sm text-slate-700">Disposable income determined?
                          <select value={meansTest.disposable_income_determined ? 'yes' : 'no'} onChange={event => setMeansTest(current => ({ ...current, disposable_income_determined: event.target.value === 'yes' }))} className="mt-1 w-full rounded-md border-slate-300">
                            <option value="yes">Yes</option><option value="no">No</option>
                          </select>
                        </label>
                        <label className="text-sm text-slate-700">Commitment period
                          <select value={meansTest.commitment_period || '3'} onChange={event => setMeansTest(current => ({ ...current, commitment_period: event.target.value }))} className="mt-1 w-full rounded-md border-slate-300">
                            <option value="3">3 years</option><option value="5">5 years</option>
                          </select>
                        </label>
                      </div>
                      {preflight.has_long_means_test && (
                        <p className="mt-3 rounded-md bg-indigo-50 px-3 py-2 text-sm text-indigo-900">Form 122C-2 will be filed afterward as its own disposable-income calculation event.</p>
                      )}
                    </section>
                  )}

                  {preflight.chapter === '13' && (
                    <section className="rounded-xl border border-slate-200 p-4">
                      <h3 className="font-semibold text-slate-900">5. Chapter 13 plan requests</h3>
                      <p className="mt-1 text-sm text-slate-600">These selections were automatically captured from the plan treatments when this filing package was created.</p>
                      <div className="mt-3 space-y-2">
                        {[
                          ['valuation', 'Valuation/payment of fully secured claims/modification of undersecured claims — Part 3.2'],
                          ['lien_avoidance', 'Lien avoidance — Part 3.4'],
                          ['surrender', 'Surrender of collateral — Part 3.5'],
                          ['executory_assumption', 'Assumption of executory contracts or unexpired leases — Part 6'],
                        ].map(([key, label]) => {
                          const included = preflight.chapter_13_plan_relief?.[key] === true;
                          return (
                            <div key={key} className="flex items-start justify-between gap-4 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                              <span className="text-slate-700">{label}</span>
                              <span className={included ? 'shrink-0 font-semibold text-green-700' : 'shrink-0 text-slate-500'}>
                                {included ? 'Selected' : 'Not selected'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  <section className="rounded-xl border border-slate-200 p-4">
                    <h3 className="font-semibold text-slate-900">{preflight.chapter === '13' ? '6' : '5'}. Filing fee paid today</h3>
                    <p className="mt-1 text-sm text-slate-600">Saved method: <strong>{preflight.filing_fee_method.replaceAll('_', ' ')}</strong>. The court accepts $0.00 when nothing is paid at filing.</p>
                    <label className="mt-3 block max-w-xs text-sm text-slate-700">Amount paid with the petition
                      <input type="number" min="0" max={preflight.filing_fee_amount} step="0.01" value={feeAmount} onChange={event => setFeeAmount(event.target.value)} disabled={preflight.filing_fee_method === 'waiver'} className="mt-1 w-full rounded-md border-slate-300 disabled:bg-slate-100" />
                    </label>
                  </section>

                  <section className="space-y-3">
                    <h3 className="font-semibold text-slate-900">Final confirmations</h3>
                    <label className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-950"><input type="checkbox" checked={filingAcknowledged} onChange={event => setFilingAcknowledged(event.target.checked)} className="mt-1" /> {selectiveRetry
                      ? 'I reviewed the selected unfinished documents and understand each selected item will be filed as a live CM/ECF docket event.'
                      : 'I reviewed the newest package, debtor names, chapter, filing fee, and DEBN choices. I understand this creates a live bankruptcy case, runs the court’s judge/trustee/341 assignment utility, and files each listed ancillary document.'}</label>
                    <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-4 text-sm text-slate-700"><input type="checkbox" checked={redactionAcknowledged} onChange={event => setRedactionAcknowledged(event.target.checked)} className="mt-1" /> I reviewed every PDF and confirm it complies with the court&apos;s redaction requirements.</label>
                    <label className="block text-sm font-medium text-slate-900">Type <strong>FILE</strong> to enable the filing button.
                      <input value={confirmationText} onChange={event => setConfirmationText(event.target.value)} className="mt-1 block w-full max-w-xs rounded-md border-slate-300" autoComplete="off" />
                    </label>
                  </section>

                  <div className="flex justify-end">
                    <button type="button" onClick={startFiling} disabled={!canStart || loading} className="inline-flex items-center gap-2 rounded-lg bg-red-700 px-5 py-3 font-bold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50">
                      <LockClosedIcon className="h-5 w-5" />
                      {loading ? 'Starting…' : selectiveRetry ? `File ${selectedStepKeys.length} selected item${selectedStepKeys.length === 1 ? '' : 's'}` : resumeRun ? 'Resume case filing' : 'Create and file the case'}
                    </button>
                  </div>
                </>
              )}

              {filing && (
                <section className={`rounded-xl border p-5 ${statusStyle(filing.status)}`}>
                  <div className="flex items-start gap-3">
                    {filing.status === 'succeeded' ? <CheckCircleIcon className="h-7 w-7 shrink-0 text-green-700" /> : ['queued', 'in_progress'].includes(filing.status) ? <ArrowPathIcon className="h-7 w-7 shrink-0 animate-spin text-indigo-700" /> : <ExclamationTriangleIcon className="h-7 w-7 shrink-0 text-amber-700" />}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-slate-900">{filing.status_display}</h3>
                      <p className="mt-1 text-sm text-slate-700">{filing.status_message}</p>
                      {filing.court_case_number && <p className="mt-1 text-sm font-semibold text-slate-900">Case {filing.court_case_number}</p>}
                      {filing.error_message && <p className="mt-2 text-sm font-medium text-red-800">{filing.error_message}</p>}
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    {(filing.steps || []).map(step => (
                      <div key={step.key} className="flex flex-col justify-between gap-1 rounded-md bg-white/80 px-3 py-2 text-sm sm:flex-row sm:items-center">
                        <span className="font-medium text-slate-800">{step.label}</span>
                        <span className={step.status === 'succeeded' ? 'font-semibold text-green-700' : step.status === 'review_required' ? 'font-semibold text-red-700' : 'text-slate-600'}>{step.status.replaceAll('_', ' ')}{step.docket_number ? ` · docket ${step.docket_number}` : ''}</span>
                      </div>
                    ))}
                  </div>

                  {filing.status === 'review_required' && (
                    <div className="mt-4 rounded-lg border border-red-300 bg-white p-4">
                      <p className="text-sm font-bold text-red-900">Do not retry until you check the live CM/ECF docket.</p>
                      {(filing.steps || []).some(step => step.key === 'initial_case' && step.status === 'review_required') && (
                        <label className="mt-3 block text-sm text-slate-700">Court case number shown on CM/ECF
                          <input value={resolvingCaseNumber} onChange={event => setResolvingCaseNumber(event.target.value)} placeholder="2:26-bk-50001" className="mt-1 block w-full max-w-sm rounded-md border-slate-300" />
                        </label>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" onClick={() => resolveUncertain('filed')} disabled={loading} className="rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800 disabled:opacity-50">I checked — it filed</button>
                        <button type="button" onClick={() => resolveUncertain('not_filed')} disabled={loading} className="rounded-md border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">I checked — it did not file</button>
                      </div>
                    </div>
                  )}

                  {filing.status === 'queued' && filing.can_cancel_queued && <button type="button" onClick={cancelQueued} disabled={loading} className="mt-4 rounded-md border border-amber-400 bg-white px-4 py-2 text-sm font-semibold text-amber-900">Cancel stalled queue request</button>}
                  {terminalStatuses.has(filing.status) && filing.status !== 'review_required' && filing.status !== 'succeeded' && (
                    <button type="button" onClick={async () => { setFiling(null); await loadPreflight(); }} className="mt-4 rounded-md bg-slate-800 px-4 py-2 text-sm font-semibold text-white">Review and resume</button>
                  )}
                  {filing.status === 'succeeded' && <button type="button" onClick={() => setOpen(false)} className="mt-4 rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white">Done</button>}
                </section>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
